import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { Messages, newMessage } from "../../model/Message";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import messagesQuery from "../../firestore/Messages.query";
import { auth } from "../../firebase";
import { Groups } from "../../model/Groups";
import { Company } from "../../model/Companies";
import { Spiner } from "../../components/spinner/Spiner";
import { ReplyResultTally } from "../../components/replyResultTally/ReplyResultTally";
import { convertDateToJapaneseString, convertTimeToString } from "../../utility/ConvertUtily";
import Button from "../../components/button/Button";
import { ConfirmAnswerModal } from "./ConfirmAnswerModalProps";
import userQuery from "../../firestore/User.query";
import companiesQuery from "../../firestore/Companies.query";
import { ReplyButton } from "../../model/ReplyButton";

export const MessageReply = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  // URLパラメータからmessageId取得
  const { messageId } = useParams<{ messageId: string }>();

  // MessageList・MessageInfo・Schedule共通
  const [isLoad, setIsLoad] = useState(true);
  const [company, setCompany] = useState<Company>();
  const [groupList, setGroupList] = useState<Groups[]>([]);
  const [replyButtonSelected, setReplyButtonSelected] = useState<ReplyButton | undefined>();
  const [topicState, setTopicState] = useState<string[]>([]);
  const [replyButtonNames, setReplyButtonNames] = useState<string[]>([]);
  const [message, setMessage]  = useState<Messages>(newMessage);
  const [isMunicipalityConfirmed, setIsMunicipalityConfirmed] = useState<boolean | undefined>(undefined);
  const [isStaffConfirmed, setIsStaffConfirmed] = useState<boolean | undefined>(undefined);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [staffConfirmed, setStaffConfirmed] = useState(false);

  const handleConfirmOpen = () => {
    setIsConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmOpen(false);
  };

  const handleConfirm = async () => {
    try {
      handleConfirmClose();

      const updatedConfirmedFlg = !isMunicipalityConfirmed;
      await messagesQuery.setConfirmed(messageId!, updatedConfirmedFlg);
      message.municipalityConfirmedFlg = updatedConfirmedFlg;
      setIsMunicipalityConfirmed(updatedConfirmedFlg);

      setMessage({...message});
    } catch (err) {
      console.error("Error updating confirmation status: ", err);
    }
  };

  // お知らせデータ作成
  useEffect(() => {
    const f = async () => {
      try{
        if(currentUser !== null && messageId){
          // 団体を取得
          const company = await companiesQuery.getCompany(currentUser.tenantId!);
          setCompany(company);

          // 団体グループ取得
          const groupList = (await companiesQuery.getGroups(company.id)).filter((group) => group.deleteFlg === false);
          setGroupList(groupList);

          // お知らせ取得
          const message = await messagesQuery.getMessage(messageId);
          message.authorName = message.authorName ?? '';

          // 返信ボタン名取得
          const replyButtonSelected = await messagesQuery.getReplyButton(messageId);
          setReplyButtonSelected(replyButtonSelected);
          const replyButtonNames = [...new Set(replyButtonSelected?.buttonName.filter((name): name is string => name !== undefined && name !== ""))];
          setReplyButtonNames(replyButtonNames);

          const topic = await messagesQuery.getTopic(messageId);
          message.topic = topic.map(t => t.id);

          if((message.select ==='token' && (message.topic?.length ?? 0) > 0)){
          message.topic = [];
          setTopicState(topicState.splice(0,Infinity));
          }
          setMessage(message);
        } else {
          throw new Error();
        }
      } catch (err :any) {
        let errorMessage = err.error;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          dispatch(alertOpen(AlertValue("お知らせを取得できません", "danger")));
        }
      }
      setIsLoad(false);
    }
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentUser?.tenantId, messageId]);

  useEffect(() => {
    const fetchMunicipalityConfirmed = async () => {
      const confirmed = await messagesQuery.getMunicipalityConfirmed(messageId!);
      setIsMunicipalityConfirmed(confirmed);
    };
    const fetchStaffConfirmed = async () => {
      const confirmed = await messagesQuery.getStaffConfirmed(messageId!);
      setIsStaffConfirmed(confirmed);
    };
    const staffCheck = async () => {
      // イベントサポート側で返信確定可能か
      if (message.select === 'topic' || (message.topic?.length ?? 0) > 0) {
        const matchingGroups = (message.topic ?? [])
          .map(topicId => groupList.find(group => group.id === topicId))
          .filter(group => group !== undefined);
        for (const group of matchingGroups) {
          const attendingGroupsDocs = await userQuery.getGroupsUser(group.id,currentUser?.tenantId!);
          const adminCount = attendingGroupsDocs.filter(attending => attending.id === group.id && attending.adminFlg).length;
          if (adminCount > 0) {
            setStaffConfirmed(true);
            break;
          }
        }
      }else{
        // 個別配信
        if(company){
          const adminFlg = await messagesQuery.getUserSearch(message.authorUid, company!.id);
          if (adminFlg) {
            setStaffConfirmed(true);
          }
        }
      }
    };
    fetchMunicipalityConfirmed();
    fetchStaffConfirmed();
    staffCheck();
  }, [messageId, company, message, groupList]);

  return (
    <main>
      <div className={"flex flex-col"}>
        <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
          <div
            className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer"
            onClick={() => navigate(`/message/${messageId}/info`)}
          >
            <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
            <span>詳細へ</span>
          </div>
        </nav>
        <div className="px-3 sm:px-6 lg:px-8">
          {isLoad ? (
            <Spiner />
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-gray-900">返信一覧</h2>
                <div className="flex space-x-2">
                  <Button size="sm" onClick={() => navigate(`/message/${messageId}/info?scrollTo=deliveryResult`)}>
                    配信先一覧
                  </Button>
                  {message?.eventFlg && (
                    <Button
                      size="sm"
                      className={!message?.eventFlg ? "opacity-50 cursor-not-allowed" : ""}
                      onClick={() => navigate(`/message/${messageId}/attendance`)}
                    >
                      出欠一覧
                    </Button>
                  )}
                  {message?.eventFlg && (
                    <Button
                      size="sm"
                      disabled={!(
                        message?.questionnaireFlg &&
                        message?.eventEndAt &&
                        new Date() > new Date(message.eventEndAt)
                      )}
                      className={!(
                        message?.questionnaireFlg &&
                        message?.eventEndAt &&
                        new Date() > new Date(message.eventEndAt)
                      ) ? "opacity-50 cursor-not-allowed" : ""}
                      onClick={() => navigate(`/message/${messageId}/questionnaireResults`)}
                    >
                      アンケート結果
                    </Button>
                  )}
                 {message?.eventFlg && (
                    <Button
                      size="sm"
                      disabled={!(message?.municipalityConfirmedFlg)}
                      className={!(message?.municipalityConfirmedFlg) ? "opacity-50 cursor-not-allowed" : ""}
                      onClick={() => navigate(`/eventParticipation/${messageId}`)}
                    >
                      イベント参加履歴一覧
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-7"></div>
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {message.title}
                </h2>
              </div>
              <div className="mt-3"></div>
              　配信日時: {convertDateToJapaneseString(new Date(message?.sendDate))} {convertTimeToString(new Date(message?.sendDate))}
              {!message.eventFlg &&staffConfirmed && isStaffConfirmed ? (
                <span className="text-black font-bold">
                  　イベントサポート：
                  <span className="text-indigo-600">返信確定済み</span>
                </span>
              ) : (!message.eventFlg &&staffConfirmed && !isStaffConfirmed ? (
                <span className="text-black font-bold">
                  　イベントサポート：
                  <span className="text-red-600">返信未確定</span>
                </span>
              )  : (!message.eventFlg &&!staffConfirmed ? (
                <span className="text-black font-bold">
                  　イベントサポート：
                  <span className="text-black-600">管理者なし</span>
                </span>
              ) : null))}
              {replyButtonSelected !== undefined ?
                <ReplyResultTally
                  currentUser={currentUser}
                  message={message}
                  replyButtonSelected={replyButtonSelected}
                  replyButtonNames={replyButtonNames}
                />
                : <></>
              }
              <div className="flex justify-end space-x-2 mt-4 mb-4">
                {!message.eventFlg && (
                  <Button onClick={handleConfirmOpen}>
                    {isMunicipalityConfirmed ? "編集確定解除" : "編集確定"}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmAnswerModal
        open={isConfirmOpen}
        toggle={handleConfirmClose}
        closeOnClickOutside={true}
        handleConfirm={handleConfirm}
        isLoad={false}
        children={undefined} 
        isMunicipalityConfirmed={isMunicipalityConfirmed ?? false}      
        isStaffConfirmed={isStaffConfirmed ?? false}  
        staffConfirmed={staffConfirmed ?? false}
      />
    </main>
  );
};